import { filterLinkedInLegacyPostFallback } from '@/biz/contentType';
import {
  getPlatformName,
  getPlatformVideoContentType,
  getPlatformVideoContentTypeDesc
} from '@/biz/platform';
import { getCreatorPlacementContentTypeLabel } from './presentation';

import { getCreatorPlacementAvailableContentTypes } from './contentTypes';
import type { CreatorContentTypePreviewPlacement } from './contentTypes';
export { getCreatorPlacementAvailableContentTypes } from './contentTypes';
export type { CreatorContentTypePreviewPlacement } from './contentTypes';

export type CreatorContentTypeDisplayMode = 'available' | 'selected';

type Translate = (key: string, options?: { defaultValue?: string }) => string;

const normalizePlatformMention = (value: string) =>
  value.toLowerCase().replace(/\s+/g, '');

const stripPlatformPrefix = (value: string, platform: string) => {
  const prefixPatterns: Record<string, RegExp> = {
    youtube: /^youtube\s+/i,
    tiktok: /^tiktok\s+/i,
    instagram: /^instagram\s+/i,
    twitter: /^twitter\s*(?:\(x\)|x)?\s*/i,
    linkedin: /^linkedin\s+/i
  };
  return value.replace(prefixPatterns[platform] ?? /^$/, '').trim();
};

export const getCreatorPricingContentTypeSummary = ({
  platform,
  contentType,
  t
}: {
  platform: string;
  contentType: string;
  t: Translate;
}) => {
  const normalizedPlatform = String(platform ?? '').toLowerCase();
  const normalizedContentType = String(contentType ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
  let compatibleContentType = contentType;

  if (normalizedContentType.includes('integrated')) {
    compatibleContentType = 'Integrated video';
  } else if (normalizedContentType.includes('dedicated')) {
    compatibleContentType = 'Dedicated video';
  } else if (normalizedPlatform === 'instagram') {
    compatibleContentType =
      normalizedContentType.includes('post') ||
      normalizedContentType.includes('carousel')
        ? 'Post'
        : 'Reels';
  } else if (normalizedPlatform === 'tiktok') {
    compatibleContentType = 'Video';
  } else if (normalizedPlatform === 'linkedin') {
    compatibleContentType = normalizedContentType.includes('video')
      ? 'Video'
      : normalizedContentType.includes('image')
        ? 'Image & Text'
        : 'Post';
  } else if (normalizedPlatform === 'twitter' || normalizedPlatform === 'x') {
    compatibleContentType = 'Post';
  }

  const label = getPlatformVideoContentType({
    platform: normalizedPlatform,
    contentType: compatibleContentType
  });
  const descriptionPlatform =
    normalizedPlatform === 'x' ? 'twitter' : normalizedPlatform;
  const platformDescription = getPlatformVideoContentTypeDesc(
    label,
    descriptionPlatform
  );

  return {
    label,
    description:
      platformDescription ||
      (normalizedPlatform === 'linkedin'
        ? t('multi_platform.quote.linkedin_post_description', {
            defaultValue:
              'A LinkedIn post created to exclusively promote a brand.'
          })
        : '')
  };
};

/** Campaign content-type preview shared by the deal card and platform tooltip. */
export const getCreatorPackageContentTypeRows = (
  placements: CreatorContentTypePreviewPlacement[],
  t: Translate,
  mode: CreatorContentTypeDisplayMode = 'available'
) => {
  const grouped = new Map<string, string[]>();
  placements
    .filter(
      (placement) =>
        placement.active !== false && placement.raw?.active !== false
    )
    .forEach((placement) => {
      const platform = placement.platform.toLowerCase();
      const contentTypes = grouped.get(platform) ?? [];
      const contentTypeCandidates =
        mode === 'selected'
          ? placement.contentType
            ? [placement.contentType]
            : []
          : getCreatorPlacementAvailableContentTypes(placement);
      contentTypeCandidates.forEach((contentType) => {
        if (!contentTypes.includes(contentType)) contentTypes.push(contentType);
      });
      grouped.set(platform, contentTypes);
    });

  return Array.from(grouped, ([platform, values]) => {
    const platformName = getPlatformName(platform);
    const displayValues = filterLinkedInLegacyPostFallback(
      platform,
      values,
      (contentType) => contentType
    );
    const contentTypes = Array.from(
      new Set(
        displayValues.map((contentType) =>
          getCreatorPlacementContentTypeLabel({ platform, contentType, t })
        )
      )
    );
    const firstContentType = contentTypes[0] ?? '';
    const firstLabel = normalizePlatformMention(firstContentType).includes(
      normalizePlatformMention(platformName)
    )
      ? firstContentType
      : `${platformName} ${firstContentType}`.trim();
    return {
      platform,
      label: [
        firstLabel,
        ...contentTypes
          .slice(1)
          .map((contentType) => stripPlatformPrefix(contentType, platform))
      ].join(` ${t('multi_platform.quote.or', { defaultValue: 'Or' })} `)
    };
  });
};
